import { useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import type { GroupMeters, MeterFrame } from '@shared/protocol';
import { getPalette, useCanvas } from '../../lib/canvas';
import { assessIntegrated, assessTruePeak, fmtDb, fmtDuration, type LoudnessTarget } from '../../lib/meterMath';
import type { LiveRefs } from '../../lib/useStreamLive';
import { Panel } from '../Panel';
import { LevelBadge } from '../Status';

const MIN = -50;
const MAX = -5;

export function groupOf(f: MeterFrame | null | undefined, id: string | null): GroupMeters | null {
  if (!f) return null;
  return f.groups.find((g) => g.id === id) ?? f.groups.find((g) => g.main) ?? f.groups[0] ?? null;
}

function LoudnessBar({
  live,
  pick,
  target,
  label,
}: {
  live: React.RefObject<LiveRefs>;
  pick: (f: MeterFrame) => number | null;
  target: LoudnessTarget;
  label: string;
}) {
  const smoothed = useRef(-Infinity);
  const ref = useCanvas((ctx, w, h) => {
    const p = getPalette();
    const f = live.current?.frame;
    const v = f ? pick(f) : null;
    // Light smoothing between 10 Hz updates.
    const t = v ?? -Infinity;
    smoothed.current = Number.isFinite(smoothed.current) && Number.isFinite(t) ? smoothed.current + (t - smoothed.current) * 0.35 : t;
    const xOf = (db: number) => Math.max(0, Math.min(1, (db - MIN) / (MAX - MIN))) * w;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = p.track;
    ctx.fillRect(0, 0, w, h);
    // Tolerance band
    ctx.fillStyle = p.graphBand;
    const b0 = xOf(target.integrated - target.tolerance);
    const b1 = xOf(target.integrated + target.tolerance);
    ctx.fillRect(b0, 0, b1 - b0, h);
    if (Number.isFinite(smoothed.current)) {
      const x = xOf(smoothed.current);
      const over = smoothed.current > target.integrated + target.tolerance;
      ctx.fillStyle = over ? p.warn : p.ok;
      ctx.fillRect(0, 2, x, h - 4);
    }
    ctx.fillStyle = p.graphTarget;
    ctx.fillRect(Math.round(xOf(target.integrated)) - 1, 0, 2, h);
  });
  return <canvas ref={ref} className="loudness-bar" role="img" aria-label={`${label} bar, target ${target.integrated} LUFS`} />;
}

export function LoudnessPanel({
  live,
  frame,
  groupId,
  target,
  onReset,
  canReset,
}: {
  live: React.RefObject<LiveRefs>;
  frame: MeterFrame | null;
  groupId: string | null;
  target: LoudnessTarget;
  onReset: () => void;
  canReset: boolean;
}) {
  const group = groupOf(frame, groupId);
  const l = group?.loudness;
  const integrated = assessIntegrated(l?.integrated ?? null, l?.elapsed ?? 0, target);
  const tp = assessTruePeak(l?.maxTruePeak ?? null, target);

  return (
    <Panel
      title={`Loudness${group && frame && frame.groups.length > 1 ? `: ${group.name}` : ''} (BS.1770-4)`}
      actions={
        <button type="button" className="btn btn-sm" onClick={onReset} disabled={!canReset} title="Restart the integrated measurement">
          <RotateCcw size={14} aria-hidden />
          Reset integration
        </button>
      }
    >
      <div className="loudness-grid">
        <div className="loudness-value">
          <span className="loudness-label">Momentary</span>
          <span className="loudness-number num">{fmtDb(l?.momentary)}</span>
          <span className="loudness-unit">LUFS</span>
          <LoudnessBar live={live} pick={(f) => groupOf(f, groupId)?.loudness.momentary ?? null} target={target} label="Momentary" />
        </div>
        <div className="loudness-value">
          <span className="loudness-label">Short-term</span>
          <span className="loudness-number num">{fmtDb(l?.shortTerm)}</span>
          <span className="loudness-unit">LUFS</span>
          <LoudnessBar live={live} pick={(f) => groupOf(f, groupId)?.loudness.shortTerm ?? null} target={target} label="Short-term" />
        </div>
        <div className="loudness-value integrated">
          <span className="loudness-label">Integrated</span>
          <span className="loudness-number num">{fmtDb(l?.integrated)}</span>
          <span className="loudness-unit">LUFS</span>
          <LevelBadge level={integrated.level}>{integrated.text}</LevelBadge>
        </div>
      </div>
      <dl className="kv loudness-stats">
        <dt>Loudness range</dt>
        <dd className="num">{l?.lra === null || l?.lra === undefined ? '--' : `${l.lra.toFixed(1)} LU`}</dd>
        <dt>Max true peak</dt>
        <dd>
          <span className="num">{fmtDb(l?.maxTruePeak)} dBTP</span> <LevelBadge level={tp.level}>{tp.text}</LevelBadge>
        </dd>
        <dt>Max momentary</dt>
        <dd className="num">{fmtDb(l?.maxMomentary)} LUFS</dd>
        <dt>Max short-term</dt>
        <dd className="num">{fmtDb(l?.maxShortTerm)} LUFS</dd>
        <dt>Integration time</dt>
        <dd className="num">{fmtDuration(l?.elapsed ?? 0)}</dd>
        <dt>Target</dt>
        <dd>
          {target.label}: {target.integrated} LUFS, tolerance {target.tolerance} LU, max {target.maxTruePeak} dBTP
        </dd>
      </dl>
    </Panel>
  );
}
